/**
 * FaceScanner — the camera panel from wireframe 3a.
 * @file src/web/features/face/components/FaceScanner.tsx
 *
 * @llm-rule WHEN: A screen needs the user to present their face
 * @llm-rule AVOID: Rendering a raw <video> elsewhere — this owns the framing, states, and coaching
 * @llm-rule NOTE: Renders the oval guide, the live indicator, and the status line.
 *   Capture is driven by the parent through the `onCapture` imperative handle.
 */

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { AlertCircle, Camera, CameraOff, Loader2 } from 'lucide-react';
import { Button } from '@bloomneo/uikit';
import { useCamera } from '../hooks/useCamera';
import {
  captureDescriptor,
  FaceCaptureError,
  loadModels,
  type CaptureResult,
} from '../lib/faceEngine';

export interface FaceScannerHandle {
  /** Grab one descriptor. Throws FaceCaptureError with coaching text. */
  capture: () => Promise<CaptureResult>;
  /** Same, plus a cropped base64 JPEG. Used once, at enrolment. */
  captureWithImage: () => Promise<CaptureResult>;
  ready: boolean;
}

interface FaceScannerProps {
  /** Caption under the oval. Defaults to the wireframe's "hold still" copy. */
  caption?: string;
  /** Compact height for the phone-sized card. */
  height?: number;
  className?: string;
}

export const FaceScanner = forwardRef<FaceScannerHandle, FaceScannerProps>(
  function FaceScanner({ caption = 'hold still — matching face', height = 206, className = '' }, ref) {
    const camera = useCamera(true);
    const [modelsLoading, setModelsLoading] = useState(true);
    const [modelError, setModelError] = useState<string | null>(null);

    useEffect(() => {
      let cancelled = false;

      loadModels()
        .then(() => {
          if (!cancelled) setModelsLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setModelsLoading(false);
          setModelError(
            err?.message?.includes('fetch')
              ? 'Face models failed to load. Check that /models is being served.'
              : err?.message || 'Face models failed to load.',
          );
        });

      return () => {
        cancelled = true;
      };
    }, []);

    const ready = camera.state === 'live' && !modelsLoading && !modelError;

    useImperativeHandle(
      ref,
      () => ({
        ready,
        capture: async () => {
          if (!camera.videoRef.current) {
            throw new FaceCaptureError('MODELS_NOT_READY', 'The camera is not running');
          }
          return captureDescriptor(camera.videoRef.current);
        },
        captureWithImage: async () => {
          if (!camera.videoRef.current) {
            throw new FaceCaptureError('MODELS_NOT_READY', 'The camera is not running');
          }
          return captureDescriptor(camera.videoRef.current, { withImage: true });
        },
      }),
      [ready, camera.videoRef],
    );

    const blocked = camera.state === 'denied' || camera.state === 'unavailable';

    return (
      <div
        className={`relative flex items-center justify-center overflow-hidden rounded-[18px] bg-[#14181f] ${className}`}
        style={{ height }}
      >
        {/* Live preview. Mirrored for the user; detection uses the raw frame. */}
        <video
          ref={camera.videoRef}
          playsInline
          muted
          autoPlay
          aria-label="Camera preview"
          className={`face-mirror absolute inset-0 size-full object-cover ${
            camera.state === 'live' ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {/* Face-framing oval, matching the wireframe's proportions. */}
        {camera.state === 'live' && (
          <div
            className={`pointer-events-none relative z-10 rounded-[50%_50%_46%_46%] border-2 border-white/55 ${
              ready ? 'face-scanning' : ''
            }`}
            style={{ width: 132, height: 168 }}
          />
        )}

        {/* Starting / loading */}
        {(camera.state === 'starting' || (camera.state === 'live' && modelsLoading)) && (
          <div className="relative z-10 flex flex-col items-center gap-2 text-white/80">
            <Loader2 className="size-5 animate-spin" />
            <span className="text-xs font-medium">
              {camera.state === 'starting' ? 'Starting camera…' : 'Loading face models…'}
            </span>
          </div>
        )}

        {/* Blocked — actionable, not a dead end */}
        {blocked && (
          <div className="relative z-10 flex max-w-[85%] flex-col items-center gap-3 text-center">
            <CameraOff className="size-6 text-white/70" />
            <p className="text-xs leading-relaxed text-white/75">{camera.error}</p>
            <Button size="sm" variant="secondary" onClick={() => void camera.start()}>
              <Camera className="mr-1.5 size-3.5" />
              Try again
            </Button>
          </div>
        )}

        {modelError && !blocked && (
          <div className="relative z-10 flex max-w-[85%] flex-col items-center gap-2 text-center">
            <AlertCircle className="size-5 text-white/70" />
            <p className="text-xs leading-relaxed text-white/75">{modelError}</p>
          </div>
        )}

        {/* Live badge */}
        {camera.state === 'live' && (
          <div className="absolute left-3.5 top-3 z-10 flex items-center gap-1.5 text-[11px] font-medium text-white">
            <span className="size-1.5 rounded-full bg-[#4ade80]" />
            camera live
          </div>
        )}

        {/* Caption */}
        {ready && (
          <div className="absolute inset-x-0 bottom-3 z-10 text-center text-xs font-medium text-white/80">
            {caption}
          </div>
        )}
      </div>
    );
  },
);
